// The journey: what happens after the customer has consented.
//
// For each bank in turn - pre-authorise if that bank needs it, create the
// consent, send the customer off to authorise, wait for the bank to say they
// did. Then fetch the accounts for every bank that got through, and offer the
// email form.
//
// It runs one bank at a time on purpose up to the authorisation: the customer
// can only be in one place at once. Only the final account fetch is done for
// all banks together.
//
// Uses WebSocketClient from bank-status.js, setBankStatus/bankDetail from
// bank-cards.js, and completeStep from stepper.js.
(function (global) {
  "use strict";

  const $ = global.$;

  function retrieveData() {
    const webSocketClient = new WebSocketClient();

    // Each phase owns its ticker. They used to share one variable in this
    // closure, so whichever started last owned it, and a missed clear left a
    // timer writing into a card that had already moved on.
    function startCountdown(frame) {
      return setInterval(() => {
        bankDetail(frame).text(`Ističe za ${webSocketClient.remaining()}`);
      }, 1000);
    }

    // A finished card is marked on its left edge, its icon and its status
    // line, not by flooding the whole card with colour - with several banks on
    // screen that read as a traffic light. What each state looks like lives in
    // the STATUS table in bank-cards.js; this only names the state.
    function settleFrame(frame, state) {
      setBankStatus(frame, state === "done" ? "done" : "failed");
    }

    function unsuccessfulFrame(frame) {
      settleFrame(frame, "failed");
    }

    function pre_auth(item, idSuffix, frame) {
      return new Promise(async (resolve, reject) => {
        try {
          $("#overlay").show();
          await new Promise((r) => setTimeout(r, 2000));

          let response = await fetch(
            "/pre_auth?bank=" + encodeURIComponent(item),
            {
              method: "GET",
            }
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          let result = await response.json();

          if (result.status === "OK" && "redirect_url" in result) {
            $("#overlay").hide();
            setBankStatus(frame, "preauthorise");
            $(frame).attr("href", result.redirect_url);

            const ticker = startCountdown(frame);
            try {
              const accessTokenAvailable = await webSocketClient
                .startListening(item, "check_pre_auth")
                .then((status) => {
                  clearInterval(ticker);
                  return status;
                })
                .catch(() => {
                  clearInterval(ticker);
                  return "rejected";
                });

              if (accessTokenAvailable === "valid") {
                resolve(true);
              } else {
                resolve(false);
              }
            } catch (error) {
              console.log("Error:", error.message);
              resolve(false);
            }
          } else if (result.status === "OK") {
            resolve(true);
          } else {
            resolve(false);
          }
        } catch (error) {
          console.log("Error:", error.message);
          reject(error);
        }
      });
    }

    async function requestConsent(item, idSuffix, frame) {
      let response = await fetch("/request_consent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": global.CSRF_TOKEN,
        },
        body: JSON.stringify({ data: item }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      let result = await response.json();

      if (result.status === "OK") {
        setBankStatus(frame, "authorise");
        $(frame).attr("href", result.redirect_url);
        $("#overlay").hide();

        const ticker = startCountdown(frame);

        try {
          const consentStatus = await webSocketClient
            .startListening(item, "check_consent_status")
            .then((status) => {
              clearInterval(ticker);
              return status;
            })
            .catch(() => {
              clearInterval(ticker);
              return "rejected";
            });

          if (consentStatus === "valid") {
            return true;
          } else {
            return false;
          }
        } catch (error) {
          return false;
        }
      } else {
        return false;
      }
    }

    function getResources(itemData, idSuffix) {
      let url = "/get_resources?data=" + encodeURIComponent(itemData);
      return new Promise((resolve, reject) => {
        fetch(url)
          .then((response) => response.json())
          .then((data) => {
            if (data.status === "OK") {
              resolve(true);
            } else {
              reject(false);
            }
          })
          .catch((error) => {
            reject(false);
          });
      });
    }

    (async function process() {
      let frames = $("a[id^='frame-']");
      let ids = [];

      for (let frame of frames) {
        var idSuffix = $(frame).attr("id").split("-")[1];
        var frameData = $(frame).parent().data("bank-name");
        const preAuthSuccessful = await pre_auth(frameData, idSuffix, frame);
        $("#overlay").hide();
        if (preAuthSuccessful) {
          $("#overlay").show();
          bankDetail(frame).text("");

          let valid = await requestConsent(frameData, idSuffix, frame);
          if (valid) {
            ids.push(idSuffix);
            setBankStatus(frame, "fetching");
            bankDetail(frame).text("");
          } else {
            unsuccessfulFrame(frame);
          }
          await new Promise((r) => setTimeout(r, 1000));
        } else {
          unsuccessfulFrame(frame);
        }
      }

      $("#overlay").show();
      await new Promise((r) => setTimeout(r, 2000));

      const fetches = ids.map(async (id) => {
        // Exact id: "a[id^='frame-1']" also matched frame-10 and frame-11.
        const frame = $("#frame-" + id);
        const itemData = $(frame).parent().data("bank-name");
        try {
          let success = await getResources(itemData, id);
          if (success) {
            settleFrame(frame, "done");
            return true;
          }
          unsuccessfulFrame(frame);
          return false;
        } catch {
          unsuccessfulFrame(frame);
          return false;
        }
      });

      // The results, not the promises. This used to test the Promise objects
      // themselves against false, which is never true - so when every bank
      // failed the customer was still offered the email form for an empty
      // report.
      const results = await Promise.all(fetches);
      $("#overlay").hide();
      if (results.every((succeeded) => succeeded !== true)) {
        $("#result").html(
          '<p class="text-danger-text">Neuspješna obrada zahtjeva!</p>'
        );
      } else {
        // The same column and the same row as the bank cards, so the form
        // is exactly as wide as a card: the card grows to fill the row, and
        // infoSpacer() holds the room the info button takes beside a bank.
        var emailForm = $("<form>", {
          id: "emailForm",
          class: "w-full max-w-xl mx-auto px-4",
        });

        var row = $("<div>", { class: "flex items-center" });

        var inputContainer = $("<div>", {
          class:
            "flex-1 basis-0 min-w-0 p-4 sm:p-5 bg-surface border border-border " +
            "rounded-card shadow-card text-left",
        });

        // Create a label for the email input
        var emailLabel = $("<label>", {
          for: "emailInput",
          class: "block text-sm font-medium",
          text: "Email za dostavu podataka",
        });

        // Side by side from sm: up. On a phone the card is now only as wide
        // as a bank card, which left the address about 150px - too short to
        // show one - so there the button goes underneath instead.
        var inputWrapper = $("<div>", {
          class: "flex flex-col sm:flex-row gap-2 mt-2.5",
        });

        // Create an input field for the email
        var emailInput = $("<input>", {
          type: "email",
          id: "emailInput",
          class:
            "flex-grow min-w-0 h-11 px-3.5 text-sm bg-surface border border-border-strong rounded-field focus:border-primary outline-none",
          placeholder: "ime.prezime@mail.hr",
          required: true,
        });

        // Create a submit button for the form
        var submitButton = $("<button>", {
          type: "submit",
          class:
            "h-11 px-6 text-sm font-semibold bg-primary hover:bg-primary-hover text-white rounded-field transition-colors",
          text: "Pošalji",
        });

        // Append the input field and submit button to the wrapper
        inputWrapper.append(emailInput, submitButton);

        // Append the label and input wrapper to the container
        inputContainer.append(emailLabel, inputWrapper);

        row.append(inputContainer, infoSpacer());
        emailForm.append(row);

        // Append the form to the result div
        $("#result").html(emailForm);

        // Handle form submission
        $("#emailForm").on("submit", function (event) {
          event.preventDefault(); // Prevent the default form submission
          $("#overlay").show();
          var email = $("#emailInput").val(); // Get the email input value
          // Create a FormData object
          var formData = new FormData();
          formData.append("email", email);

          // Send the email data to the backend (Python) for processing
          $.ajax({
            url: "/send_mail", // Replace with your server endpoint to handle email sending
            method: "POST",
            processData: false,
            contentType: false,
            data: formData,
            success: function (response) {
              completeStep(3);
              $("#result").html(
                '<p class="text-success-text font-medium">Zahtjev je uspješno obrađen!</p>'
              );
            },
            error: function (error) {
              $("#result").html(
                '<p class="text-danger-text">Došlo je do pogreške!</p>'
              );
            },
            complete: function () {
              $("#overlay").hide();
            },
          });
        });
      }
    })();
  }

  const api = { retrieveData };
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
  Object.assign(global, api);
})(typeof window !== "undefined" ? window : globalThis);
